/**
 * @fileoverview SettingsAppShell — outermost frame of the Control Panel (the
 * Multipass-internal settings app). Two slots:
 *
 *  - `menu`: SettingsAppMenu (left rail of category headers + entries)
 *  - `children`: the active settings page (rendered inside the content area)
 *
 * IT WEARS FOUNDRY'S CLASSES NOW. The mapping note here said
 * `settings-app__app-wrapper`, a namespace that does not appear in any capture —
 * the real one is `control-panel-shell__settings*`, read off foundry-ui's five
 * Control Panel captures. The rules are vendored into
 * styles/vendor/foundry-control-panel.generated.css and apply by construction.
 *
 * THE OTHER SHELL IS DEAD. `components/control-panel/settings-shell.tsx` exports
 * a `SettingsShell` with the same job, referenced now only by two doc comments —
 * nothing renders it. Wiring the classes into that one first changed nothing on
 * the deployed page, which is how it was found. It is left in place rather than
 * deleted in the same breath as a feature change, but it is not the shell.
 *
 * `.bp-control-panel` is what Foundry's Blueprint TOKENS are scoped to — see the
 * generated stylesheet's header.
 */

import type { ReactNode } from "react";
import { controlPanelShell as fdy } from "../control-panel/foundry-parts.generated";
import { LoadingBar } from "../../packages/control-panel-shell/src/components/LoadingBar";

interface SettingsAppShellProps {
  /** Left-rail menu — typically <SettingsAppMenu />. */
  menu: ReactNode;
  /** Active page content (header + body). */
  children: ReactNode;
  className?: string;
}

export function SettingsAppShell({ menu, children, className }: SettingsAppShellProps) {
  return (
    /*
      THE NESTING, FROM FOUNDRY'S OWN RULES rather than from the names:

        __settings-wrapper__   flex COLUMN, 100% x 100%   the frame
        __settings__           flex ROW, flex: 1 1 0      sidebar | content
        __settings-sidebar__   flex: 0 0 230px            the rail
        __settings-content__   flex: 1 1 auto, scrolls    the page

      I had the first two the other way round on the reading that "wrapper"
      wraps "settings", and the rail rendered as a full-width block stacked
      above the content with the page scrolling sideways. The rule text says
      which is the row; the names do not.

      THE SIDEBAR IS ONE ELEMENT, NOT TWO. This wrapped the menu in a
      `div.__settings-sidebar__` on the reading that Foundry's rail is "a div
      holding sections". The DOM diff against the tenant says otherwise: the
      rail is a single `ul` carrying BOTH `__settings-sidebar__` and
      `__sidebar__`, holding `ul.__sidebar-section__` children. The extra div
      shifted every following sibling, so a large share of the structural
      differences were one wrong element repeated down the tree. The menu owns
      those classes now and is rendered here directly.

      The loading bar is Foundry's first child of `__settings__`, and it has a
      job here: the extension registry code-splits eleven settings pages, so a
      rail click is a chunk fetch.
    */
    <div
      data-testid="settings-app-shell"
      className={["bp-control-panel", fdy["settings-wrapper"], "bp-settings-app-wrapper", className]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        data-test-id="settings-container"
        className={`${fdy["settings"]} bp-settings-app`}
      >
        <LoadingBar />
        {menu}
        {/* `__container__` wraps `__settings-content__` — see the note in
            routes/control-panel.tsx; same correction, same snapshot. */}
        <div className={fdy["container@t7drfl"]}>
          <div className={`${fdy["settings-content"]} bp-settings-app-content`}>{children}</div>
        </div>
      </div>
    </div>
  );
}
